import { computed, effect, Injectable, untracked } from '@angular/core';
import {
    setCurrentActiveIntegrations,
    setCurrentOrganizationId,
    setOrganization,
    setOrganizations,
    setServices,
} from '../../../../store/global/global.actions';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import {
    OrganizationSettingsObject,
} from '@libs/types/src/organization/object-type/organization/organization-settings.object';
import { ComponentStoreV2Factory } from '../../../shared/store/common/component-store-v2.factory';
import { getOrganizationSettingsFrag, getOrganizationsFrag } from './organization-select.query';
import { InfiniteScrollStore } from '../../../shared/store/common/infinite-scroll.store';
import { OrganizationGridItemObject } from '@it2go/types';
import { servicesDownloaded } from '../../../../store/services';

@Injectable()
export class OrganizationSelectStore extends ComponentStoreV2Factory {

    public organizationScroll = computed(() =>
        new InfiniteScrollStore<OrganizationGridItemObject>(
            this.api.getOrganizations,
            FilterBuilder.filter({ limit: 100, sort: { column: 'name', direction: 'ASC' } }),
            true,
            getOrganizationsFrag,
            undefined,
            true,
        ),
    );

    constructor() {
        super();

        Promise.all([
            this.api.getSelectedOrganizationId(),
            this.api.getOrganizations(FilterBuilder.filter({ limit: 1_000 }), getOrganizationsFrag),
        ]).then(([selectedOrganizationId, result]) => {
            const organizations: OrganizationGridItemObject[] = result.items || [];
            const currentOrganizationId = this.globalGlobalStore.organizationStore.selectedId();
            const initialOrganizationId = organizations.find((organization) => organization.id === selectedOrganizationId)?.id
                ?? organizations.find((organization) => organization.id === currentOrganizationId)?.id
                ?? organizations[0]?.id
                ?? 0;

            this.store.dispatch(setOrganizations({ organizations }));
            this.setOrganizationId(initialOrganizationId);
        });

        effect(() => {
            this.store!.dispatch(setOrganization({ organization: this.data.getOrganization() }));
        });

        effect(() => {
            const settings = this.data.getOrganizationSettings()?.['items'] || [];

            this.globalGlobalStore.organizationStore.settings.set(settings);
            this.store!.dispatch(setCurrentActiveIntegrations({
                activeIntegrations: (settings)
                    .filter((i: OrganizationSettingsObject) => i.active)
                    .reduce((acc: Record<string, { data: string }>, i: OrganizationSettingsObject) => {
                        acc[i.key] = i;
                        return acc;
                    }, {}),
            }));
        });

        effect(() => {
            const services = this.data.getServices();
            this.globalGlobalStore.organizationStore.services.set(services?.['items'] || []);
            servicesDownloaded.set(!!services);
            this.store!.dispatch(setServices({ services: this.data.getServices()?.['items'] || [] }));
        });

        effect(() => {
            this.globalGlobalStore.organizationStore.allServices.set(this.data.getAllServices()?.['items'] || []);
        });

        this.doOnActions(
            () => this.api.getOrganizationSettings(
                FilterBuilder.singlePage({ organizationId: this.globalGlobalStore.organizationStore.selectedId() }),
                getOrganizationSettingsFrag,
            ),
            'getOrCreateOrganizationSetting',
            'removeOrganizationSetting',
            'updateOrganizationSetting',
        );

        this.doOnActions(
            () => {
                // TODO solve delay
                setTimeout(() => this.organizationScroll().refresh(), 2_000);
            },
            'createOrganization',
            'updateOrganization',
            'deleteOrganization',
        );

        this.doOnActions( // nelze mít onSUccess, protože services stránka používá starý store
            () => {
                this.api.getServices(FilterBuilder.singlePage({ organizationId: this.globalGlobalStore.organizationStore.selectedId() }));
                this.api.getAllServices();
            },
            'createService',
            'updateService',
            'deleteService',
            'deleteWorkplace',
            'updateWorkplace',
            'createWorkplace',
            'createServiceBasicInfo',
            'updateServiceBasicInfo',
            'createWorkplaceAddress',
            'updateWorkplaceAddress',
            'deleteWorkplaceAddress',
        );

        this.onSuccess(
            () => {
                this.api.getServices(FilterBuilder.singlePage({ organizationId: this.globalGlobalStore.organizationStore.selectedId() }));
                this.api.getAllServices();
            },
            'updateServiceSettings',
        );
    }

    public setOrganizationId(organizationId: number): void {
        untracked(() => {
            const currentId = this.globalGlobalStore.organizationStore.selectedId();
            if (!organizationId || (currentId === organizationId && servicesDownloaded())) return;

            if (currentId != organizationId) {
                this.globalGlobalStore.invalidateAcl();
                this.api.setSelectedOrganizationId({ organizationId }).then();
            }

            this.globalGlobalStore.organizationStore.selectedId.set(organizationId);
            this.store.dispatch(setCurrentOrganizationId({ organizationId }));
            this.api.getAllServices();
            this.api.getServices(FilterBuilder.singlePage({ organizationId }))
                .then((services) => {
                    const selectedServiceId = this.globalGlobalStore.loggedUserStore.selectedServiceId();

                    if (!selectedServiceId || services?.items?.find((service: any) => service.id === selectedServiceId)) {
                        return;
                    }

                    this.globalGlobalStore.loggedUserStore.setSelectedServiceId(services?.items?.[0]?.id || -1).then();
                });
            this.api.getOrganization({ id: organizationId, validAt: null, centerId: null }).then();
            this.api.getOrganizationSettings(FilterBuilder.singlePage({ organizationId }), getOrganizationSettingsFrag).then();
        });
    }

}
